import { memo } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Handshake } from 'lucide-react'

import voiHotelsLogo from '@/assets/images/partners/LogoVOIHotels.png'
import igvLogo from '@/assets/images/partners/IGV-Logo.png'
import veratourLogo from '@/assets/images/partners/VeraTourLogo.png'
import bluserenaLogo from '@/assets/images/partners/LogoBluserena.png'
import mangiasLogo from '@/assets/images/partners/MangiasLogo.png'
import palladiumLogo from '@/assets/images/partners/PalladiumHotelGroupLogo.png'
import ahrLogo from '@/assets/images/partners/AHRLogo.png'
import bravoClubLogo from '@/assets/images/partners/BravoClubLogo-1.png'
import buoneVacanzeLogo from '@/assets/images/partners/BuoneVacanzeLogo.png'
import sirenisLogo from '@/assets/images/partners/SirenisHotelsResorts.png'
import grupotelLogo from '@/assets/images/partners/GrupotelLogo.png'
import thResortsLogo from '@/assets/images/partners/TH-Resorts-logo-2.png'

const partners = [
  { name: 'VOI Hotels', logo: voiHotelsLogo },
  { name: 'IGV', logo: igvLogo },
  { name: 'Veratour', logo: veratourLogo },
  { name: 'Bluserena', logo: bluserenaLogo },
  { name: 'Mangias', logo: mangiasLogo },
  { name: 'Palladium Hotel Group', logo: palladiumLogo },
  { name: 'AHR', logo: ahrLogo },
  { name: 'Bravo Club', logo: bravoClubLogo },
  { name: 'Buone Vacanze', logo: buoneVacanzeLogo },
  { name: 'Sirenis Hotels', logo: sirenisLogo },
  { name: 'Grupotel', logo: grupotelLogo },
  { name: 'TH Resorts', logo: thResortsLogo },
]

const PartnerLogo = memo(function PartnerLogo({
  partner,
  index,
}: {
  partner: { name: string; logo: string }
  index: number
}) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.3, delay: index * 0.03 }}
      className="group relative flex items-center justify-center p-3 sm:p-4 md:p-6 bg-white rounded-lg sm:rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200 transition-all duration-300"
    >
      <img
        src={partner.logo}
        alt={partner.name}
        className="max-h-10 sm:max-h-12 md:max-h-16 w-auto object-contain grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-300"
        loading="lazy"
      />
    </motion.div>
  )
})

export function PartnersSection() {
  const { t } = useTranslation('common')

  return (
    <section className="section-padding bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-1/2 left-0 w-96 h-96 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 opacity-50 blur-3xl -translate-x-1/2 -translate-y-1/2" />

      <div className="container-custom relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-gray-700 border border-gray-300 shadow-sm mb-4">
            <Handshake className="w-4 h-4" />
            <span className="text-sm font-medium">{t('partners.label')}</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('partners.title')}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('partners.description')}
          </p>
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4 md:gap-6">
          {partners.map((partner, index) => (
            <PartnerLogo key={partner.name} partner={partner} index={index} />
          ))}
        </div>
      </div>
    </section>
  )
}
